/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useMutation } from '@apollo/client';
import { Avatar, Box, Button, Flex, FormLabel, Text, useDisclosure, useToast, Wrap, WrapItem } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import moment from 'moment';
import 'moment/locale/en-gb';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import FileFormField from '../../components/form/FileFormField';
import RadioFormField from '../../components/form/RadioFormField';
import LoadingOverlayText from '../../components/LoadingOverlay/LoadingOverlayText';
import c from '../../constant';
import DateTimeFormField from '../../utils/DateTimeFormField';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusSquare, faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import LabelDetail from '../../components/LabelDetail';
import SelectUserAllModal from '../../components/Modal/SelectUserShowAllModal';
import useStore from '../../store';
import TextFormField from '../../components/form/TextFormField';
import CheckBoxFormField from '../../components/form/CheckBoxFormField';

const CREATE_MUTATION = gql`
	mutation create(
		$users: [String!]!
		$courseTitle: String!
		$organiser: String!
		$certificate: String!
		$dateStart: DateTime!
		$dateEnd: DateTime!
		$location: String!
		$means: TrainingRecordEnumMeans!
		$locationType: TrainingRecordEnumLocationType!
		$type: TrainingRecordEnumType!
		$attachmentFile: Upload
		$attachmentFileSize: Float
		$bioListing: Boolean
	) {
		createTrainingRecord(
			recordInput: {
				users: $users
				courseTitle: $courseTitle
				organiser: $organiser
				certificate: $certificate
				dateStart: $dateStart
				dateEnd: $dateEnd
				location: $location
				means: $means
				locationType: $locationType
				type: $type
				bioListing: $bioListing
			}
			attachmentFile: $attachmentFile
			attachmentFileSize: $attachmentFileSize
		) {
			id
			courseTitle
			organiser
			certificate
			dateStart
			dateEnd
			location
			means
			type
			locationType
			attachmentFile
			bioListing
		}
	}
`;

const LABEL_WIDTH = '169px';

const validationSchema = Yup.object().shape({
	users: Yup.array().min(1, 'required').required('required'),
	type: Yup.string().required('required'),
	locationType: Yup.string().required('required'),
	means: Yup.string().required('required'),
	courseTitle: Yup.string().required('required'),
	organiser: Yup.string().required('required'),
	dateStart: Yup.date().required('required'),
	dateEnd: Yup.date()
		.required('required')
		.test('is-after-start-date', 'End date cannot be before start date', function (value) {
			const { dateStart } = this.parent;

			if (!value || !dateStart) return false;

			return moment(value).isSameOrAfter(moment(dateStart), 'day');
		}),

	location: Yup.string().required('required'),
	bioListing: Yup.boolean(),
	attachmentFile: Yup.mixed().test('fileSize', 'over limit', (value) => {
		if (value) {
			return value?.size <= c.maxFileSize / 20;
		}

		return true;
	}),
});

function TrainingRecordAdd({ onCloseModal }) {
	moment.locale('en-gb');
	const cancelRef = React.useRef();
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const history = useHistory();
	const toast = useToast();
	const { t } = useTranslation();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [openSelectUserAllModel, setOpenSelectUserAllModel] = useState(false);
	const [officials, setOfficials] = useState([]);
	const [createMutation, { loading }] = useMutation(CREATE_MUTATION);

	useEffect(() => window.scrollTo(0, 0), []);

	const dgHideCreateButton = user?.position === '1';

	async function submit(values) {
		const selectedUsers = officials?.map((item) => item?.id).filter(Boolean);

		const variables = {
			users: selectedUsers,
			courseTitle: values?.courseTitle,
			organiser: values?.organiser,
			certificate: values?.certificate,
			dateStart: moment(values?.dateStart).toDate(),
			dateEnd: moment(values?.dateEnd).toDate(),
			location: values?.location,
			means: values?.means,
			locationType: values?.locationType,
			type: values?.type,
			attachmentFile: values?.attachmentFile ? values?.attachmentFile : null,
			attachmentFileSize: values?.attachmentFile?.size ? values?.attachmentFile?.size : null,
			bioListing: values?.bioListing,
		};

		try {
			const res = await createMutation({ variables });

			const createdId = res?.data?.createTrainingRecord?.id;

			if (!createdId) {
				throw new Error('TRAINING_RECORD_NOT_CREATED');
			}

			toast({
				title: t('Training record created successfully'),
				status: 'success',
				isClosable: true,
				duration: 5000,
				position: 'top-right',
			});

			if (onCloseModal) {
				onCloseModal();
			}

			history.push(`/training-course`);
		} catch (error) {
			toast({
				title: t(error?.message ? error?.message : 'something went wrong'),
				status: 'error',
				duration: 5000,
				isClosable: true,
				position: 'bottom-right',
			});
		}
	}

	return (
		<>
			<Box w="100%" bg="white" borderRadius={c.borderRadius} ml="1px" pb="32px">
				<Box maxW="510px">
					<Formik
						initialValues={{
							users: user?.id ? [user?.id] : [],
							type: '',
							locationType: '',
							means: '',
							courseTitle: '',
							certificate: '',
							organiser: '',
							dateStart: new Date(),
							dateEnd: new Date(),
							reason: '',
							location: '',
							attachmentFile: null,
							bioListing: false,
						}}
						validationSchema={validationSchema}
						onSubmit={submit}
					>
						{(props) => {
							const { values } = props;
							return (
								<Box>
									<Form>
										<FormLabel>{t('Officials')}</FormLabel>

										<Flex>
											<LabelDetail
												mb={1}
												mt={0}
												labelWidth={LABEL_WIDTH}
												childDetail={
													<Wrap>
														{officials?.map((item, index) => {
															return (
																<Flex
																	direction="column"
																	justifyContent="center"
																	alignItems="center"
																	key={item?.id || 'u-' + index}
																	mr={2}
																>
																	<WrapItem className="avatarWraper">
																		<FontAwesomeIcon
																			onClick={() => {
																				const newOfficials = officials.filter((u) => u?.id !== item?.id);

																				setOfficials(newOfficials);

																				props.setFieldValue(
																					'users',
																					newOfficials.map((u) => u?.id),
																				);

																				toast({
																					title: t('You have removed a user'),
																					status: 'warning',
																					isClosable: true,
																					position: 'top-right',
																				});
																			}}
																			className="remove"
																			icon={faMinusSquare}
																			style={{
																				fontSize: 18,
																				display: 'none',
																				position: 'absolute',
																				zIndex: '100',
																				marginLeft: 32,
																				color: 'red',
																				backgroundColor: 'white',
																				padding: 2,
																				cursor: 'pointer',
																			}}
																		/>

																		<Avatar
																			name={item?.fullNameKhmer || item?.fullName}
																			src={item?.avatar?.icon ? c.baseFilePath + item?.avatar?.icon : null}
																		/>
																	</WrapItem>

																	<Box mt={1}>
																		<Text textAlign="center">{lang === 'kh' ? item?.fullNameKhmer : item?.fullName}</Text>
																	</Box>
																</Flex>
															);
														})}

														{!dgHideCreateButton && (
															<Flex>
																<Button
																	className="hidden-print"
																	type="button"
																	onClick={() => setOpenSelectUserAllModel('viewer')}
																	leftIcon={<FontAwesomeIcon icon={faPlusSquare} style={{ fontSize: 18 }} />}
																	colorScheme="blue"
																	variant="outline"
																>
																	{t('Add')}
																</Button>
															</Flex>
														)}
													</Wrap>
												}
											/>
										</Flex>

										{openSelectUserAllModel && (
											<SelectUserAllModal
												cached={false}
												onClose={() => setOpenSelectUserAllModel(false)}
												callback={(e) => {
													if (openSelectUserAllModel === 'viewer') {
														const existed = officials?.some((item) => item?.id === e?.id);

														if (existed) {
															toast({
																title: t('This user has been added'),
																status: 'warning',
																isClosable: true,
																position: 'top-right',
																duration: 3000,
															});

															setOpenSelectUserAllModel(false);
															return;
														}

														const newOfficials = [...officials, e];

														setOfficials(newOfficials);

														props.setFieldValue(
															'users',
															newOfficials.map((item) => item?.id),
															true,
														);

														toast({
															title: t('You have added a user'),
															status: 'success',
															isClosable: true,
															position: 'top-right',
														});
													}

													setOpenSelectUserAllModel(false);
												}}
												viewType="all"
											/>
										)}

										<RadioFormField
											label="Location Type"
											name="locationType"
											radios={[
												{
													label: 'Local',
													value: 'LOCAL',
												},
												{
													label: 'Overseas',
													value: 'OVERSEAS',
												},
											]}
											required
										/>

										<RadioFormField
											label="Type"
											name="type"
											radios={[
												{
													label: 'Training',
													value: 'TRAINING',
												},
												{
													label: 'Seminar',
													value: 'SEMINAR',
												},
											]}
											required
										/>

										<TextFormField {...props} label="Course Title" name="courseTitle" required />

										<TextFormField {...props} label="Organiser" name="organiser" required />

										<TextFormField {...props} label="Certificate" name="certificate" />

										<DateTimeFormField
											{...props}
											label={t('Date From')}
											name="dateStart"
											value={values?.dateStart}
											years={true}
											nextTwoYear={false}
											required
										/>

										<DateTimeFormField
											{...props}
											label={t('Date To')}
											name="dateEnd"
											value={values?.dateEnd}
											years={true}
											nextTwoYear={false}
											required
										/>

										<TextFormField {...props} label="location" name="location" required />

										<RadioFormField
											label="Means"
											name="means"
											radios={[
												{
													label: 'ONSITE',
													value: 'ONSITE',
												},
												{
													label: 'Online',
													value: 'ONLINE',
												},
											]}
											required
										/>

										<FileFormField {...props} label="Attachment File" name="attachmentFile" maxFileSize={c.maxFileSize / 20} />

										<CheckBoxFormField {...props} label="List Course to Bio" name="bioListing" />

										<Button
											id="login-btn"
											variant="solid"
											size="md"
											h="42px"
											w="128px"
											mt={4}
											type="submit"
											borderRadius={c.borderRadius}
											borderColor="red.400"
											isLoading={loading}
											_focus={{ boxShadow: 'none' }}
										>
											{t('Submit')}
										</Button>
									</Form>
								</Box>
							);
						}}
					</Formik>
				</Box>
			</Box>

			{loading && <LoadingOverlayText />}
		</>
	);
}

export default TrainingRecordAdd;
